import { getStoreId, setStoreId } from "$/hooks/use-store-information"
import storeQueries from "$/lib/queries/store-queries"
import { userQueryOptions } from "$/lib/queries/user-queries"
import { toast } from "@repo/ui/hooks/use-toast"
import { QueryClient } from "@tanstack/react-query"
import { isNoAccessError } from "./ensure-job-store-selected"
import { loadTranslator } from "./load-translator"

/**
 * The admin app (admin.julo.se) opens Alfred with `?storeId=` to jump into a
 * store. Switches to it when the user has access; otherwise stays in the
 * current store.
 */
export async function ensureRequestedStoreSelected(
  queryClient: QueryClient,
  storeId: string,
): Promise<void> {
  const [user, currentStoreId] = await Promise.all([
    queryClient.ensureQueryData(userQueryOptions),
    getStoreId(),
  ])

  if (storeId === currentStoreId) return

  const t = await loadTranslator(queryClient)

  const hasRole = user.roles.some((role) => role.storeId === storeId)
  if (!hasRole && !user.isAdmin) {
    toast({
      title: t("alfred.shell.storeAccess.noStoreAccess"),
      variant: "destructive",
    })
    return
  }

  // An admin without a role in the store only has it listed once it has been
  // fetched; a store that doesn't exist must not become the selected one.
  // No retries: a 404 is an answer, and retrying it delays the page by seconds.
  const store = await queryClient
    .fetchQuery({ ...storeQueries.detail(storeId), retry: false })
    .catch((error: Error) => {
      if (isNoAccessError(error)) return null
      throw error
    })

  if (!store) {
    toast({
      title: t("alfred.shell.storeAccess.storeNotFound"),
      variant: "destructive",
    })
    return
  }

  await setStoreId(storeId)
  // Same reset as the store switcher: every cached query belongs to the old
  // store, including the store information the sidebar is built from.
  queryClient.removeQueries()
  toast({
    title: t("alfred.shell.storeAccess.switchedStore", {
      storeName: store.name,
    }),
  })
}
